import {
  SPARKY_FRAMEWORK,
  ADMIN_CLUSTER_URL_NAME,
  API_SERVICE_URLS_NAME,
  API_GATEWAY_URL_KEY,
  API_GW_GLOBAL_SVC_URL_KEY,
  APP_REGISTRY_URL_KEY,
  INSTANCE_STORE_URL_KEY,
  TENANCY_SERVICE_URL_KEY,
  SUBSCRIPTION_SERVICE_URL_KEY,
  IAM_SERVICE_URL_KEY,
  IDENTITY_SERVICE_URL_KEY,
  REGIONAL_URL_KEYS,
  CDL_APP_ID,
  FAWKES_APP_ID,
  PA_PANORAMA_APP_ID,
  FAWKES_MICRO_APP_NAME,
  SDWAN_MICRO_APP_NAME,
  SDWAN_APP_ID,
  SDWAN_PATH_PREFIX,
  COSMOS_URL_KEY,
  PREFERENCE_SERVICE_URL_KEY,
  SCM_CLIENT_IDS,
} from './constants'
import actions from './actions'
import { stateRef } from './store'
import type { SparkySettings } from './microAppTypes'
import type { DeferredPromise, Authed, MainState, FrameworkVars, ServiceOptions, AppsCanBeLaunched, AppCanBeLaunched, AuthState, AuthedCallback } from './stateTypes'
import type { Instance } from './indexedArrays'

export type InitedPromise = DeferredPromise<SparkySettings> & { settings: SparkySettings }

const createDeferredPromise = <T>(): DeferredPromise<T> => {
  const deferred = {} as any
  const promise = new Promise((res, rej) => {
    deferred.resolve = (result: any) => {
      promise.result = result
      res(result)
    }
    deferred.reject = (error: any) => {
      promise.error = error
      rej(error)
    }
  }) as DeferredPromise<T>
  return Object.assign(promise, deferred)
}

export const inited = createDeferredPromise<SparkySettings>() as InitedPromise
export const authed = createDeferredPromise<Authed>()
export const loaded = createDeferredPromise<MainState>()
export const authenticated = createDeferredPromise<AuthState>()
// export const authroized = createDeferredPromise<AuthState>()

export const getInitSettings = (): SparkySettings => inited.settings

const getFrameworkVarFn: (<T = any>(name?: string) => T) | (() => FrameworkVars) = (name?: string) => {
  const vars = inited.settings?.variables?.[SPARKY_FRAMEWORK] as FrameworkVars
  return vars && name ? vars[name] : (vars || {})
}
export const getFrameworkVar = getFrameworkVarFn as <T = any>(name?: string) => T | undefined
export const getFrameworkVars = getFrameworkVarFn as () => FrameworkVars
export const getOidcClientId = (): string | undefined => {
  const { oidc_client_id, oidc_client } = getFrameworkVars()
  return oidc_client_id || oidc_client?.client_id || undefined
}
export const isOidcClient = (clientId: string | undefined): boolean => {
  if (!clientId) {
    return false
  }
  const currentClientId = getOidcClientId()!
  if (currentClientId === clientId) {
    return true
  }
  if (SCM_CLIENT_IDS.includes(clientId)) {
    return SCM_CLIENT_IDS.includes(currentClientId)
  }
  return false
  // return Boolean(clientId) && getOidcClientId() === clientId
}

export const hasMicroApp = (name: string): boolean => {
  return Boolean(name && inited.settings?.microAppNameSet?.has(name))
}

const APPS_DEFAULT_LAUNCH_CFG = {
  [FAWKES_APP_ID]: { app_id: FAWKES_APP_ID }, // auto
  [SDWAN_APP_ID]: { app_id: SDWAN_APP_ID, default_route: SDWAN_PATH_PREFIX },
} as AppsCanBeLaunched
const appsCanBeLaunched = {} as AppsCanBeLaunched
export const getAppCanBeLaunched = (appId: string | null | undefined): AppCanBeLaunched | undefined => {
  if (!appId || getFrameworkVar('hub')) {
    return
  }
  if (!getOidcClientId()) { // non-tsg for fawkes only
    return APPS_DEFAULT_LAUNCH_CFG[FAWKES_APP_ID]
  }
  if (appsCanBeLaunched[FAWKES_APP_ID]) { // cached
    return appsCanBeLaunched[appId]
  }
  const appsVar = getFrameworkVar<Record<string, AppCanBeLaunched | null>>('apps_can_launch')
  if (!appsVar || !Object.keys(appsVar).length) { // auto default
    if (getFrameworkVar('fawkes_session') || hasMicroApp(FAWKES_MICRO_APP_NAME)) {
      appsCanBeLaunched[FAWKES_APP_ID] = APPS_DEFAULT_LAUNCH_CFG[FAWKES_APP_ID]
    }
    if (hasMicroApp(SDWAN_MICRO_APP_NAME)) {
      appsCanBeLaunched[SDWAN_APP_ID] = APPS_DEFAULT_LAUNCH_CFG[SDWAN_APP_ID]
    }
  }
  else {
    for (const appId of Object.keys(appsVar)) {
      const app = appsVar[appId]
      if (app) {
        app.app_id = appId
        appsCanBeLaunched[appId] = app
      }
    }
    if (appsCanBeLaunched[FAWKES_APP_ID]) { // use prisma_access config for pa_panorama
      appsCanBeLaunched[PA_PANORAMA_APP_ID] = appsCanBeLaunched[FAWKES_APP_ID]
    }
  }
  return appsCanBeLaunched[appId]
}

export const isAppCanBeLaunched = (appIdOrInstance: Instance | string | null | undefined): boolean => {
  if (getFrameworkVar('hub')) {
    return false
  }
  const appId = (appIdOrInstance as Instance)?.app_id || appIdOrInstance as string | null | undefined
  if (!appId) {
    return false
  }
  if (!getOidcClientId()) { // non-tsg for fawkes only
    return appId === FAWKES_APP_ID
  }
  const appCanLaunchCfg = getAppCanBeLaunched(appId)
  if (!appCanLaunchCfg) {
    return false
  }
  if (typeof appIdOrInstance === 'string') {
    return true
  }
  const { client_id, url } = appIdOrInstance as Instance
  if (client_id && appCanLaunchCfg.client_id && appCanLaunchCfg.client_id !== client_id) {
    return false
  }
  if (appCanLaunchCfg.origin) {
    let originCfg = appCanLaunchCfg.origin.toLowerCase() as string
    // if (originCfg === 'location-origin') {
    //   originCfg = window.location.origin
    // }
    originCfg = originCfg.startsWith('https://') ? originCfg : `https://${originCfg}`
    const origin = url?.startsWith('http') && new URL(url).origin.toLowerCase()
    if (origin && originCfg !== origin) {
      return false
    }
  }
  return true
}

export const getCurrentPlatformRegion = (preferredAppId?: string): string | undefined => {
  const tsgRegion = stateRef.current.auth?.instances?.getPlatformRegion?.(preferredAppId)
  if (tsgRegion) {
    return tsgRegion
  }
  // for non-tsg env
  const tenantInfo = stateRef.current.main?.tenant?.info
  if (preferredAppId?.startsWith(FAWKES_APP_ID)) {
    return tenantInfo?.region
  }
  const tenantRegion = tenantInfo?.associations?.find((asso: { app_id: string }) => asso.app_id === CDL_APP_ID)?.region || tenantInfo?.region
  return tenantRegion
}
const API_GW_PATH_MAP = {
  [INSTANCE_STORE_URL_KEY]: '/subscription/v1',
  [SUBSCRIPTION_SERVICE_URL_KEY]: '/subscription/v1',
  [TENANCY_SERVICE_URL_KEY]: '/tenancy/v1',
  [IAM_SERVICE_URL_KEY]: '/iam/v1',
  [IDENTITY_SERVICE_URL_KEY]: '/identity/v1',
  [PREFERENCE_SERVICE_URL_KEY]: '/preference/api/v1',
  [APP_REGISTRY_URL_KEY]: '/app-registry',
} as Record<string, string | undefined>
const getRegionalUrl = (name: string, options?: ServiceOptions) => {
  const region = options?.region || getCurrentPlatformRegion()
  if (!region) {
    return
  }
  const urlMapping = getFrameworkVar(API_SERVICE_URLS_NAME)?.[name] || {}
  return urlMapping[region] || urlMapping.default
}
export const getFrameworkServiceUrl = (name: string, options?: ServiceOptions): string | undefined => {
  if (name === COSMOS_URL_KEY) { // temp behavior due to some edge cases require API returned value
    const ensureHttps = (fqdn?: string) => (!fqdn || fqdn.startsWith('https:') ? fqdn : `https://${fqdn}`)
    return ensureHttps(stateRef.current.main?.fqdnInfo?.msg?.paifqdn ||
      getRegionalUrl(name, options) || window.__insights_api_server)
  }
  if (REGIONAL_URL_KEYS.includes(name)) {
    return getRegionalUrl(name, options)
  }
  const urls = getFrameworkVar(API_SERVICE_URLS_NAME) || {}
  if (name === ADMIN_CLUSTER_URL_NAME) {
    return stateRef.current.auth?.fawkes_admin_api_url ||
      stateRef.current.main?.tenant?.info?.runtime_attributes?.admin_api_url ||
      getFrameworkVar(name) || urls.default_admin_cluster_url || window.__admin_cluster_url
  }
  if (name === API_GW_GLOBAL_SVC_URL_KEY) {
    return urls[API_GW_GLOBAL_SVC_URL_KEY] || urls[API_GATEWAY_URL_KEY]
  }
  if (getFrameworkVar('use_api_gateway') !== false) {
    const apiPath = API_GW_PATH_MAP[name]
    if (apiPath?.startsWith('/')) {
      const apiGwUrl = urls[API_GW_GLOBAL_SVC_URL_KEY] || urls[API_GATEWAY_URL_KEY]
      if (!apiGwUrl) {
        return undefined
      }
      const url = new URL(apiPath, apiGwUrl).toString()
      API_GW_PATH_MAP[name] = url
      return url
    }
    if (apiPath) {
      return apiPath
    }
  }
  return urls[name]
}

export const onAuthed = (cb: AuthedCallback) => {
  // cb no return
  authed.then(cb).catch((err) => {
    console.error('prepare load info error', err)
  })
}

export { stateRef, actions }
export { history } from './history'
export * from './store'
export { events } from './events'
